import { useEffect, useMemo, useState } from 'react';
import type { McqExtra } from '@yorukiri/shared';
import { useTranslation } from '@yorukiri/i18n';
import { colors } from '@yorukiri/ui/tokens';

type Props = {
  question: string;
  extra: McqExtra;
  revealed: boolean;
  onReveal: (wasCorrect: boolean) => void;
};

// Interactive MCQ review widget.
// Before `revealed`: user selects option(s); hitting Submit (or Space) tells the
// parent whether the selection was correct and flips the card into reveal mode.
// After `revealed`: shows every option marked correct/incorrect plus the explanation.
export function McqPlayer({ question, extra, revealed, onReveal }: Props) {
  const { t } = useTranslation('review');
  const multi = extra.correct.length > 1;
  const correct = useMemo(() => new Set(extra.correct), [extra.correct]);

  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!revealed) setSelected(new Set());
  }, [revealed, question]);

  const toggle = (i: number) => {
    if (revealed) return;
    setSelected((prev) => {
      if (multi) {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i);
        else next.add(i);
        return next;
      }
      return new Set([i]);
    });
  };

  const submit = () => {
    if (revealed || selected.size === 0) return;
    const same =
      selected.size === correct.size && [...selected].every((s) => correct.has(s));
    onReveal(same);
  };

  useEffect(() => {
    if (revealed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && ['INPUT', 'TEXTAREA'].includes(e.target.tagName))
        return;
      const n = Number(e.key);
      if (!Number.isNaN(n) && n >= 1 && n <= extra.options.length) {
        e.preventDefault();
        toggle(n - 1);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed, selected, extra.options.length]);

  return (
    <div style={{ maxWidth: 780, width: '100%' }}>
      <div style={questionStyle}>{question}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {extra.options.map((option, i) => {
          const isSelected = selected.has(i);
          const isCorrect = correct.has(i);
          const state: OptionState = revealed
            ? isCorrect
              ? 'correct'
              : isSelected
                ? 'wrong'
                : 'neutral'
            : isSelected
              ? 'selected'
              : 'idle';
          return (
            <button
              key={i}
              onClick={() => toggle(i)}
              disabled={revealed}
              style={optionStyle(state)}
            >
              <span style={marker(state, multi)}>
                {state === 'correct' ? '✓' : state === 'wrong' ? '✗' : i + 1}
              </span>
              <span style={{ flex: 1, textAlign: 'left', color: colors.ink }}>{option}</span>
            </button>
          );
        })}
      </div>

      {!revealed && (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={submit} disabled={selected.size === 0} style={submitBtn(selected.size > 0)}>
            {t('mcq_submit')}
          </button>
          <span style={{ fontSize: 11, color: colors.ink3 }}>
            {multi ? t('mcq_hint_multi') : t('mcq_hint_single')}
          </span>
        </div>
      )}

      {revealed && extra.explanation && (
        <div style={explanation}>
          <div style={explanationLabel}>{t('mcq_explanation')}</div>
          {extra.explanation}
        </div>
      )}
    </div>
  );
}

type OptionState = 'idle' | 'selected' | 'correct' | 'wrong' | 'neutral';

const questionStyle: React.CSSProperties = {
  fontWeight: 600,
  letterSpacing: '-0.015em',
  fontSize: 24,
  lineHeight: 1.3,
  color: colors.ink,
  marginBottom: 20,
};

function optionStyle(state: OptionState): React.CSSProperties {
  const borderColor =
    state === 'correct'
      ? colors.good
      : state === 'wrong'
        ? colors.bad
        : state === 'selected'
          ? colors.accent
          : colors.line2;
  const background =
    state === 'correct'
      ? 'rgba(52, 168, 83, 0.08)'
      : state === 'wrong'
        ? 'rgba(232, 69, 69, 0.06)'
        : state === 'selected'
          ? colors.accentSoft
          : colors.surface;
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    border: `1px solid ${borderColor}`,
    background,
    borderRadius: 9,
    fontFamily: 'inherit',
    fontSize: 14,
    cursor: state === 'correct' || state === 'wrong' || state === 'neutral' ? 'default' : 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.12s, background 0.12s',
  };
}

function marker(state: OptionState, multi: boolean): React.CSSProperties {
  const color =
    state === 'correct'
      ? colors.good
      : state === 'wrong'
        ? colors.bad
        : state === 'selected'
          ? colors.accent
          : colors.ink3;
  return {
    width: 24,
    height: 24,
    borderRadius: multi ? 4 : '50%',
    border: `1px solid ${color}`,
    color,
    background: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  };
}

const explanation: React.CSSProperties = {
  marginTop: 16,
  padding: '12px 14px',
  background: colors.surface2,
  borderLeft: `2px solid ${colors.accent}`,
  borderRadius: '0 7px 7px 0',
  fontSize: 13,
  color: colors.ink2,
  lineHeight: 1.5,
};

const explanationLabel: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontSize: 9,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: colors.ink3,
  marginBottom: 4,
  fontWeight: 500,
};

function submitBtn(enabled: boolean): React.CSSProperties {
  return {
    background: enabled ? colors.ink : colors.surface2,
    color: enabled ? colors.surface : colors.ink3,
    border: `1px solid ${enabled ? colors.ink : colors.line2}`,
    padding: '9px 18px',
    borderRadius: 7,
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    cursor: enabled ? 'pointer' : 'not-allowed',
  };
}
